import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useAccountId } from '../../../../redux/slices/appInfo/hooks';
import { useGetAccountQuery } from '../../../../redux/api/fymAPI';
import { ErrorAlert } from '../../../common/views/ErrorAlert/ErrorAlert';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../../common/views/LoadingView';
import { ProfileIdentityCard } from '../../../smartLinks/profile/ProfileIdentityCard';
import { ProfileSaveBar } from '../../../smartLinks/profile/ProfileSaveBar';
import { NoArtistCard } from './NoArtistCard';
import { artistSettingsMessages } from './artistSettingsMessages';
import { useArtistForm } from './useArtistForm';
import { ArtistTimeZoneCard } from './timeZone/ArtistTimeZoneCard';
import { isDemoAccount } from '../../../accountSelector/demoAccounts';
import { demoArtistMessages } from '../../../accountSelector/demoArtistMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import cards from '../settingsCards.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Artist and SmartLinks parts; each part uses its own slice of it */
import styles from './artistSettings.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * Settings → Artist (design artist-settings a1, a2): the avatar and the display name of the
 * active artist, the one the artist switcher shows. It replaces the old Edit Profile page.
 */
const ArtistTabComponent: React.FC = () => {
  const intl = useIntl();
  const accountId = useAccountId();
  const { data, error, isLoading, refetch } = useGetAccountQuery(
    { accountId: accountId ?? 0 },
    { skip: accountId === null }
  );
  const account = data?.account;
  // design: music24-demo-artist a5. The demo artist is read-only: no edit, no save.
  const isDemo = isDemoAccount(account);
  const form = useArtistForm(account);

  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);
  const { save } = form;
  const onSave = useCallback(() => {
    save().catch(console.error);
  }, [save]);

  if (accountId === null) {
    return (
      <div className={cards.pane}>
        <NoArtistCard />
      </div>
    );
  }
  if (error !== undefined) {
    return (
      <div className={cards.pane}>
        <ErrorView error={error} retry={retry} />
      </div>
    );
  }
  if (isLoading || account === undefined) {
    return (
      <div className={cards.pane}>
        <LoadingView />
      </div>
    );
  }

  return (
    <div className={cards.pane}>
      {form.error !== undefined && <ErrorAlert error={form.error} />}
      <ProfileIdentityCard
        accountId={account.id}
        name={form.values.name}
        avatar={form.values.avatar}
        onNameChange={form.setName}
        onAvatarChange={form.setAvatar}
        disabled={form.isSaving || isDemo}
      />
      <p className={styles.footNote}>
        {intl.formatMessage(isDemo ? demoArtistMessages.settingsReadOnly : artistSettingsMessages.activeArtistNote)}
      </p>
      <ArtistTimeZoneCard accountId={account.id} readOnly={isDemo} />
      {!isDemo && (
        <ProfileSaveBar
          dirtyCount={form.dirtyCount}
          isAddressDirty={false}
          canSave={form.canSave}
          isSaving={form.isSaving}
          onDiscard={form.discard}
          onSave={onSave}
          saveLabel={intl.formatMessage(artistSettingsMessages.saveArtist)}
        />
      )}
    </div>
  );
};

export const ArtistTab = TypedMemo(ArtistTabComponent);
